import type {Vector2} from 'three'

/**
 * Client coordinates -> the canvas's own 0..1 / NDC space.
 *
 * `window.innerWidth/innerHeight` is NOT the same quantity and must not be used
 * here. NDC spans the WebGL drawing buffer, which CSS stretches to the canvas's
 * border box, so the only correct denominator is that box. On a phone
 * `XRExtras.FullWindowCanvas` makes the two agree - it sets the canvas to
 * width/height 100% of a body it also sets to 100% - but:
 *
 *  - on iOS Safari `window.innerHeight` is the VISUAL viewport and drifts from
 *    the layout box while the URL bar animates. A few tens of pixels barely move
 *    a floor point; on a 1.2 m character it is the difference between hitting
 *    the head and missing into the sky.
 *  - on /debug the canvas is 480 px inside a page with 16 px of padding, where
 *    the two are not even close - which is what would otherwise make the tap
 *    interaction impossible to exercise without a phone.
 *
 * 8th Wall's own screen-to-ray helper does the same thing
 * (`(e.clientX - canvas.clientLeft) / canvas.clientWidth`, xr.js). This form is
 * strictly better: `clientLeft` is a border width, `rect.left` is the actual
 * viewport offset.
 *
 * `null` on a zero-sized rect (mid orientation flip, or a `display: none`
 * canvas) rather than handing NaN to the raycaster, where it would silently
 * become a dead tap instead of a skipped one.
 */
export const clientTo01 = (
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number
): readonly [number, number] | null => {
  // Forces layout, so this belongs on a tap and nowhere near the frame loop.
  const rect = canvas.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return null
  return [(clientX - rect.left) / rect.width, (clientY - rect.top) / rect.height]
}

/** As above, in normalised device coordinates. Writes into `target`, no allocation. */
export const clientToNdc = (
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number,
  target: Vector2
): Vector2 | null => {
  const uv = clientTo01(canvas, clientX, clientY)
  if (uv === null) return null
  return target.set(uv[0] * 2 - 1, -uv[1] * 2 + 1)
}
